
import React, { useEffect, useState } from "react";

const CONSENT_COOKIE_NAME = "cookie_preferences_v1";
const CONSENT_COOKIE_EXPIRY_DAYS = 180;
const CONSENT_VERSION = "2026-06";

const COOKIE_CATEGORIES = [
  {
    key: "supportChat",
    title: "Support Chat",
    description:
      "Enables customer support, FAQs and assistance through Tawk.to.",
    required: false,
    details: [
         {
        name: "twk_#",
        host: "embed.tawk.to",
        duration: "Persistent",
        type: "Third Party",
        category: "Statistic Cookies",
        description: "Used by tawk.to live chat widget to manage visitor connections. It prevents duplicate connection requests when a visitor navigates across multiple pages or opens the site in several browser tabs, ensuring the chat widget remains stable and synchronized throughout the session. This cookie expires at the end of the browser session and does not store any personal information."
        },
        {
        name: "twk_idm_key",
        host: "embed.tawk.to",
        duration: "Persistent",
        type: "Third Party",
        category: "Statistic Cookies",
        description: "Allows the website to recognise the visitor, in order to optimize the chat-box functionality."
        },
        {
        name: "twk_uuid_#",
        host: "embed.tawk.to",
        duration: "Persistent",
        type: "Third Party",
        category: "Statistic Cookies",
        description: "Used by tawk.to live chat widget to recognise returning visitors. It assigns each visitor a randomly generated unique identifier (UUID) that is stored on their device. This cookie does not store any personally identifiable information, but enables a more personalised chat experience by remembering previous visits."
        },
        {
        name: "twk_token_#",
        host: "embed.tawk.to",
        duration: "Persistent",
        type: "Third Party",
        category: "Preference Cookies",
        description: "Used by tawk.to live chat widget to store a session authentication token in the browser's local storage. This token keeps the chat session active and authenticated as the visitor navigates between pages on the website. The identifier in the name corresponds to the tawk.to property ID configured for this site. No personally identifiable information is stored in this token. It is cleared when the visitor ends the browser session"
        },
        {
        name: "previousNav",
        host: "embed.tawk.to",
        duration: "Session",
        type: "Third Party",
        category: "Statistic Cookies",
        description: "Registers statistical data on users' behaviour on the website. Used for internal analytics by the website operator."
        },
        {
        name: "TawkConnectionTime",
        host: "embed.tawk.to",
        duration: "Session",
        type: "Third Party",
        category: "Statistic Cookies",
        description:
            "Allows the website to recognise the visitor in order to optimise chat functionality."
        }
    ]
  },

  // Future examples:
  // {
  //   key: "analytics",
  //   title: "Analytics",
  //   description: "Helps us understand website usage and improve our service.",
  //   required: false,
  // },
  // {
  //   key: "marketing",
  //   title: "Marketing",
  //   description: "Allows personalised marketing and advertising.",
  //   required: false,
  // },
];

function getDefaultPreferences() {
  const prefs = {};

  COOKIE_CATEGORIES.forEach((category) => {
    prefs[category.key] = true;
  });

  return prefs;
}

/**
 * Cookie Helpers
 */
function setConsentCookie(preferences) {
  const expires = new Date();
  expires.setDate(
    expires.getDate() + CONSENT_COOKIE_EXPIRY_DAYS
  );

  document.cookie = [
    `${CONSENT_COOKIE_NAME}=${encodeURIComponent(
      JSON.stringify(preferences)
    )}`,
    `expires=${expires.toUTCString()}`,
    "path=/",
    "SameSite=Lax",
    window.location.protocol === "https:"
      ? "Secure"
      : "",
  ]
    .filter(Boolean)
    .join("; ");
}

function getConsentCookie() {
  const cookie = document.cookie
    .split("; ")
    .find((row) =>
      row.startsWith(
        `${CONSENT_COOKIE_NAME}=`
      )
    );

  if (!cookie) {
    return null;
  }

  try {
    const value = cookie.substring(
      `${CONSENT_COOKIE_NAME}=`.length
    );

    return JSON.parse(
      decodeURIComponent(value)
    );
  } catch {
    return null;
  }
}

export function clearConsentCookie() {
  document.cookie =
    `${CONSENT_COOKIE_NAME}=; ` +
    "expires=Thu, 01 Jan 1970 00:00:00 GMT; " +
    "path=/";
}

/**
 * Tawk Helpers
 */
function loadTawkTo() {
  const existing =
    document.getElementById("tawk-script");

  if (existing) {
    existing.remove();
  }

  window.Tawk_API = {};
  window.Tawk_LoadStart = new Date();

  const script =
    document.createElement("script");

  script.id = "tawk-script";
  script.async = true;
  script.src =
    process.env.REACT_APP_TAWK_KEY;
  script.charset = "UTF-8";
  script.setAttribute(
    "crossorigin",
    "*"
  );

  document.body.appendChild(script);
}

function deleteCookie(name) {
  const domains = [
    "",
    window.location.hostname,
    `.${window.location.hostname}`,
  ];

  domains.forEach((domain) => {
    const domainPart = domain
      ? `domain=${domain}; `
      : "";

    document.cookie =
      `${name}=; ` +
      "expires=Thu, 01 Jan 1970 00:00:00 GMT; " +
      `${domainPart}` +
      "path=/";
  });
}

function removeTawkCookies() {
  const cookies =
    document.cookie.split(";");

  cookies.forEach((cookie) => {
    const name = cookie
      .split("=")[0]
      .trim();

    if (
      name
        .toLowerCase()
        .includes("tawk") ||
      name.toLowerCase().includes("twk")
    ) {
      deleteCookie(name);
    }
  });
}

export function unloadTawkTo() {
  try {
    if (
      window.Tawk_API &&
      typeof window.Tawk_API
        .hideWidget === "function"
    ) {
      window.Tawk_API.hideWidget();
    }
  } catch (error) {
    console.warn(
      "Unable to hide Tawk widget",
      error
    );
  }

  const script =
    document.getElementById(
      "tawk-script"
    );

  if (script) {
    script.remove();
  }

  removeTawkCookies();

  window.Tawk_API = undefined;
  window.Tawk_LoadStart = undefined;
}



/**
 * Component
 */
export default function CookieConsentForm() {

    const [expandedCategories, setExpandedCategories] =useState({});
    const [visible, setVisible] = useState(false);

    const [preferences, setPreferences] =
        useState(
        getDefaultPreferences()
    );
    const toggleExpanded = (categoryKey) => {
  setExpandedCategories((current) => ({
    ...current,
    [categoryKey]: !current[categoryKey],
  }));
};

  useEffect(() => {
    const existing =
      getConsentCookie();

    if (
      !existing ||
      existing.consentVersion !==
        CONSENT_VERSION
    ) {
      setVisible(true);
    } else {
      const loadedPrefs =
        getDefaultPreferences();

      COOKIE_CATEGORIES.forEach(
        (category) => {
          loadedPrefs[
            category.key
          ] =
            existing[
              category.key
            ] ?? false;
        }
      );

      setPreferences(
        loadedPrefs
      );

      if (
        existing.supportChat
      ) {
        loadTawkTo();
      }
    }

    const handleShowCookieSettings =
      () => {
        setVisible(true);
      };

    window.addEventListener(
      "show-cookie-settings",
      handleShowCookieSettings
    );

    return () => {
      window.removeEventListener(
        "show-cookie-settings",
        handleShowCookieSettings
      );
    };
  }, []);

  const saveConsent = (
    prefs
  ) => {
    const cookiePreferences = {
      necessary: true,
      consentedAt:
        new Date().toISOString(),
      consentVersion:
        CONSENT_VERSION,
      ...prefs,
    };

    setConsentCookie(
      cookiePreferences
    );

    if (
      cookiePreferences.supportChat
    ) {
      loadTawkTo();
    } else {
      unloadTawkTo();
    }

    setVisible(false);
  };

  const acceptAll = () => {
    const allAccepted = {};

    COOKIE_CATEGORIES.forEach(
      (category) => {
        allAccepted[
          category.key
        ] = true;
      }
    );

    setPreferences(
      allAccepted
    );
    saveConsent(
      allAccepted
    );
  };

  const acceptNecessaryOnly =
    () => {
      const allRejected =
        {};

      COOKIE_CATEGORIES.forEach(
        (category) => {
          allRejected[
            category.key
          ] = false;
        }
      );

      setPreferences(
        allRejected
      );
      saveConsent(
        allRejected
      );
    };

  const saveSelectedPreferences =
    () => {
      saveConsent(
        preferences
      );
    };

  const togglePreference = (
    key
  ) => {
    setPreferences(
      (current) => ({
        ...current,
        [key]:
          !current[key],
      })
    );
  };

  if (!visible) {
    return null;
  }

  return (
    <div style={styles.overlay}>
      <div style={styles.container}>
        <h2 style={styles.title}>
          Cookie Preferences
        </h2>

        <p style={styles.intro}>
          We use cookies to
          provide secure access
          to your account and
          optional features such
          as customer support.
          You can choose which
          optional cookies you
          would like to allow.
        </p>

        <div
          style={
            styles.cookieCard
          }
        >
          <div
            style={
              styles.cookieInfo
            }
          >
            <h3
              style={
                styles.heading
              }
            >
              Necessary Cookies
            </h3>

            <p
              style={
                styles.text
              }
            >
              Required to
              authenticate users,
              maintain secure
              login sessions and
              provide core portal
              functionality.
              These cookies
              cannot be disabled.
            </p>
          </div>

          <div
            style={
              styles.requiredBadge
            }
          >
            Always Active
          </div>
        </div>

        {COOKIE_CATEGORIES.map(
          (category) => (
            <div
  key={category.key}
  style={styles.cookieCard}
>
  <div style={styles.cookieInfo}>
    <div style={styles.headerRow}>
      <div>
        <h3 style={styles.heading}>
          {category.title}
        </h3>

        <p style={styles.text}>
          {category.description}
        </p>
      </div>

      <label style={styles.switch}>
        <input
          type="checkbox"
          checked={
            preferences[category.key]
          }
          onChange={() =>
            togglePreference(category.key)
          }
          style={{ display: "none" }}
        />

        <span
          style={{
            ...styles.slider,
            background:
              preferences[category.key]
                ? "#2563eb"
                : "#d1d5db",
          }}
        >
          <span
            style={{
              ...styles.sliderHandle,
              transform:
                preferences[category.key]
                  ? "translateX(24px)"
                  : "translateX(0)",
            }}
          />
        </span>
      </label>
    </div>

    <button
      type="button"
      style={styles.detailsButton}
      onClick={() =>
        toggleExpanded(category.key)
      }
    >
      {expandedCategories[category.key]
        ? "Hide Details"
        : "View Details"}
    </button>

    {expandedCategories[category.key] && (
      <div style={styles.detailsContainer}>
        {category.details?.map(
          (cookie, index) => (
            <div
              key={index}
              style={styles.cookieDetail}
            >
              <div style={styles.detailRow}>
                <strong>Name:</strong>
                <span>{cookie.name}</span>
              </div>

              <div style={styles.detailRow}>
                <strong>Host:</strong>
                <span>{cookie.host}</span>
              </div>

              <div style={styles.detailRow}>
                <strong>Duration:</strong>
                <span>{cookie.duration}</span>
              </div>

              <div style={styles.detailRow}>
                <strong>Type:</strong>
                <span>{cookie.type}</span>
              </div>

              <div style={styles.detailRow}>
                <strong>Category:</strong>
                <span>{cookie.category}</span>
              </div>

              <div style={styles.detailDescription}>
                {cookie.description}
              </div>
            </div>
          )
        )}
      </div>
    )}
  </div>
</div>
          )
        )}

        <div
          style={
            styles.buttonContainer
          }
        >
          <button
            style={
              styles.primaryButton
            }
            onClick={
              acceptAll
            }
          >
            Accept All
          </button>

          <button
            style={
              styles.secondaryButton
            }
            onClick={
              acceptNecessaryOnly
            }
          >
            Necessary Only
          </button>

          <button
            style={
              styles.saveButton
            }
            onClick={
              saveSelectedPreferences
            }
          >
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Styling
 */
const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    background:
      "rgba(0,0,0,0.45)",
    display: "flex",
    justifyContent:
      "center",
    alignItems:
      "flex-end",
    padding: "16px",
  },

  container: {
    width: "100%",
    maxWidth: "900px",
    background:
      "#ffffff",
    borderRadius:
      "16px",
    padding: "24px",
    boxShadow:
      "0 20px 40px rgba(0,0,0,0.15)",
    border:
      "1px solid #e5e7eb",
    maxHeight: "90vh",
    overflowY: "auto",
  },

  title: {
    margin:
      "0 0 12px 0",
    fontSize:
      "1.5rem",
    fontWeight: 700,
  },

  intro: {
    color: "#4b5563",
    lineHeight: 1.6,
    marginBottom: "24px",
  },

  cookieCard: {
    display: "flex",
    justifyContent:
      "space-between",
    alignItems:
      "center",
    gap: "20px",
    padding: "18px",
    marginBottom:
      "12px",
    border:
      "1px solid #e5e7eb",
    borderRadius:
      "12px",
  },

  cookieInfo: {
    flex: 1,
  },

  heading: {
    margin:
      "0 0 6px 0",
    fontSize: "1rem",
    fontWeight: 600,
  },

  text: {
    margin: 0,
    color: "#4b5563",
    lineHeight: 1.5,
  },

  requiredBadge: {
    background:
      "#dbeafe",
    color: "#1d4ed8",
    padding:
      "8px 12px",
    borderRadius:
      "999px",
    fontSize: "0.85rem",
    fontWeight: 600,
    whiteSpace:
      "nowrap",
  },

  switch: {
    position:
      "relative",
    display:
      "inline-block",
    width: "52px",
    height: "28px",
    flexShrink: 0,
  },

  slider: {
    position:
      "absolute",
    inset: 0,
    borderRadius:
      "999px",
    transition:
      "all 0.2s ease",
  },

  sliderHandle: {
    position:
      "absolute",
    top: "2px",
    left: "2px",
    width: "24px",
    height: "24px",
    background:
      "#ffffff",
    borderRadius:
      "50%",
    transition:
      "all 0.2s ease",
    boxShadow:
      "0 1px 3px rgba(0,0,0,0.2)",
  },

  buttonContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "12px",
    marginTop: "24px",
  },

  primaryButton: {
    flex: "1 1 200px",
    padding:
      "12px 18px",
    border: "none",
    borderRadius:
      "8px",
    background:
      "#2563eb",
    color: "#ffffff",
    fontWeight: 600,
    cursor: "pointer",
  },

  secondaryButton: {
    flex: "1 1 200px",
    padding:
      "12px 18px",
    border:
      "1px solid #d1d5db",
    borderRadius:
      "8px",
    background:
      "#ffffff",
    color: "#111827",
    fontWeight: 600,
    cursor: "pointer",
  },

  saveButton: {
    flex: "1 1 200px",
    padding:
      "12px 18px",
    border: "none",
    borderRadius:
      "8px",
    background:
      "#111827",
    color: "#ffffff",
    fontWeight: 600,
    cursor: "pointer",
  },
  headerRow: {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "16px",
},

detailsButton: {
  marginTop: "12px",
  padding: "8px 12px",
  border: "none",
  background: "transparent",
  color: "#2563eb",
  cursor: "pointer",
  fontWeight: 600,
},

detailsContainer: {
  marginTop: "16px",
  borderTop: "1px solid #e5e7eb",
  paddingTop: "16px",
},

cookieDetail: {
  padding: "12px",
  marginBottom: "12px",
  background: "#f9fafb",
  borderRadius: "8px",
  border: "1px solid #e5e7eb",
},

detailRow: {
  display: "flex",
  gap: "8px",
  marginBottom: "6px",
  flexWrap: "wrap",
},

detailDescription: {
  marginTop: "8px",
  color: "#4b5563",
  lineHeight: 1.5,
},
};
